// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Divider,
  Grid,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom';
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import parse from "html-react-parser";
import dayjs from 'dayjs'
import {
  GetReferBookEntryDetail,
  GetReferBookEntryTokenDetail,
} from '../../services/ReferBookEntryService';
import logo from "../../assets/images/logo.png"
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper';

// COMMON STYLES
const value = {
  color: "#000",
  marginLeft: "4px",
};

const filedsLabelText = {
  color: "#00000099",
  fontSize: "14px",
  padding: "0px 0px 0px 5px !important"
};

interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  doctorId: number;
  fullName: string;
  qualification: string;
}

const ReferBookReport = () => {
  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    fullName: '',
    qualification: '',
    doctorId: 0
  };

  const edService = new EncryptDecryptService();
  const navigate = useNavigate();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [referBookData, setReferBookData] = useState<any>();
  const [branchDetails, setBranchDetails] = useState<BranchDetails>(initialBranchDetails);
  const smallScreen = useMediaQuery("(max-width:768px)");

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = '';

  if (token !== null && token !== undefined && token !== '') {
    currentToken = token;
  } else {
    navigate('/not-found');
  }

  const getReferBookTokenDetail = async () => {
    let tokenData: any = null;
    try {
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetReferBookEntryTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;
        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          doctorId: data.doctorId ?? 0,
          branchId: data.branchId ?? 0,
          fullName: data.fullName ?? '',
          qualification: data.qualification ?? '',
          referBookEntryId: data.referBookEntryId ?? 0
        };

        setBranchDetails(tokenData);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      return tokenData;
    }
  };

  const getReferBookEntryDetailById = async (refBookId: string) => {
    let ReferBookEntry: any;
    let params = new URLSearchParams();
    params.append("referBookEntryId", refBookId);

    try {
      const res: any = await GetReferBookEntryDetail(params, currentToken);
      if (res) {
        let result = res?.data?.data;
        setReferBookData(result);
      }
      return ReferBookEntry;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getReferBookTokenDetail();
      if (tokenData && tokenData?.referBookEntryId > 0) {
        await getReferBookEntryDetailById(tokenData?.referBookEntryId?.toString());
      } else {
        navigate('/not-found');
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [currentToken]);

  return (
    <>
      <SkeletonWrapper
        isLoading={isLoading}
        className="skeleton-input"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "96vh",
            paddingY: 3,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            className={"main-container"}
            sx={{
              width: "100%",
              maxWidth: 900,
              backgroundColor: "#fff",
              borderRadius: "10px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
              p: 3,
              marginX: "auto",
            }}
          >
            <Grid item xs={12} mb={1.5}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "18px !important",
                  marginLeft: "4px",
                  textAlign: "center"
                }}
              >
                {referBookData?.organizationName}
              </Typography>

              {/* Organization Address Details */}
              <Grid
                style={{
                  textAlign: "center",
                  paddingTop: "10px",
                  fontSize: "13px",
                  color: "#444",
                }}
              >
                <Grid>
                  {[
                    referBookData?.address1,
                    referBookData?.address2,
                    referBookData?.city,
                  ]
                    .filter(Boolean)
                    .join(", ")}{" "}
                  {referBookData?.postalCode
                    ? ` - ${referBookData?.postalCode}`
                    : ""}
                </Grid>

                {(referBookData?.mobileNumber ||
                  referBookData?.email) && (
                    <Grid style={{ marginTop: "4px" }}>
                      {[
                        referBookData?.mobileNumber
                          ? `Contact Number: ${referBookData?.mobileNumber}`
                          : "",
                        referBookData?.email
                          ? `Email: ${referBookData?.email}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Grid>
                  )}
              </Grid>
            </Grid>

            <Divider sx={{ mb: 1 }} />

            <Grid item xs={12} mb={1.5}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "18px !important",
                  marginLeft: "4px"
                }}
              >
                {referBookData?.toDoctorName}{" "}
                {referBookData?.toDoctorQualification && (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px !important",
                    }}
                  >
                    ({referBookData.toDoctorQualification})
                  </Typography>
                )}
              </Typography>
            </Grid>

            <Divider sx={{ mb: 1 }} />

            <Grid item xs={12} mb={1}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  fontSize: "16px !important",
                  marginLeft: "4px"
                }}
              >
                {referBookData?.toLocationName}
              </Typography>

              {/* Address Line */}
              <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
                {[
                  referBookData?.toLocationAddress1,
                  referBookData?.toLocationAddress2,
                  referBookData?.toLocationCity,
                  referBookData?.toLocationCountry
                    ? `${referBookData?.toLocationCountry} - ${referBookData?.toLocationPostalCode}`
                    : referBookData?.toLocationPostalCode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>

              {/* Mobile Numbers */}
              {(referBookData?.toLocationContactNumber ||
                referBookData?.fromLocationAlternateContactNumber ||
                referBookData?.toLocationEmail) && (
                  <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
                    {[
                      referBookData?.toLocationContactNumber
                        ? `Contact Number: ${referBookData?.toLocationContactNumber}`
                        : "",
                      referBookData?.fromLocationAlternateContactNumber
                        ? `${referBookData?.fromLocationAlternateContactNumber}`
                        : "",
                      referBookData?.toLocationEmail
                        ? `Email: ${referBookData?.toLocationEmail}`
                        : "",
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                )}
            </Grid>

            <Divider sx={{ mb: 1 }} />
{/* Available Date */}
            {referBookData?.referBookAvailableDateJson && (() => {
              try {
                const dates = JSON.parse(referBookData.referBookAvailableDateJson);
                if (dates?.length > 0) {
                  return (
                    <Box>
                      <Typography sx={filedsLabelText}>
                        Next working day/hours:
                      </Typography>

                      {dates.map(
                        (item: any, index: number) => (
                          <Typography key={index} variant="body1" sx={value}>
                            {item.dayName},{" "}
                            {dayjs(item.appointmentDate).format(
                              "MMM DD, YYYY"
                            )}{" "} - {item.timeSlot}
                          </Typography>
                        ))}
                    </Box>
                  );
                }
              } catch { }
              return null;
            })()}

            <Divider sx={{ mb: 1, mt: 1 }} />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography sx={filedsLabelText}>
                  Ref Date:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {referBookData?.referDate
                    ? dayjs(referBookData.referDate).format("DD-MM-YYYY")
                    : ""}
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={1} mt={0.1}>
              <Grid item xs={6}>
                <Typography sx={filedsLabelText}>
                  Patient First Name:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {referBookData?.forPatientFirstName}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography sx={filedsLabelText}>
                  Patient Last Name:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {referBookData?.forPatientLastName}
                </Typography>
              </Grid>

              {(referBookData?.forPatientGender > 0 ||
                referBookData?.forPatientAgeYear > 0 ||
                referBookData?.forPatientAgeMonth > 0 ||
                referBookData?.forPatientAgeDay > 0) && (

                  <Grid item xs={6}>
                    <Typography sx={filedsLabelText}>
                      GENDER / AGE:
                    </Typography>

                    <Typography variant="body1" sx={value}>
                      {referBookData?.forPatientGender || null}
                      {(referBookData?.forPatientAgeYear > 0 ||
                        referBookData?.forPatientAgeMonth > 0 ||
                        referBookData?.forPatientAgeDay > 0) ? ' / ' : ''}

                      {referBookData?.forPatientAgeYear > 0
                        ? `${referBookData.forPatientAgeYear}y `
                        : ''}
                      {referBookData?.forPatientAgeMonth > 0
                        ? `${referBookData.forPatientAgeMonth}m `
                        : ''}
                      {referBookData?.forPatientAgeDay > 0
                        ? `${referBookData.forPatientAgeDay}d`
                        : ''}
                    </Typography>
                  </Grid>
                )}

              {referBookData?.forPatientMobileNumber && (
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>
                    Patient Mobile Number:
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.forPatientMobileNumber}
                  </Typography>
                </Grid>
              )}
            </Grid>


            {referBookData?.reasonForReferenceColl?.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <Typography sx={filedsLabelText}>
                  Reason for Reference:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {referBookData?.reasonForReferenceColl
                    .map((item: any) => item?.reasonForReferenceLabel)
                    .join(", ")}
                </Typography>
              </Grid>
            )}

            {referBookData?.clinicalNotes && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <Typography sx={filedsLabelText}>
                  Clinical Notes:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {parse(
                    (referBookData?.clinicalNotes || "")
                      .replace(/^<p>/, "")
                      .replace(/<\/p>$/, "")
                  )}
                </Typography>
              </Grid>
            )}

            <Divider sx={{ mb: 1, mt: 1 }} />

            {(referBookData?.fromDoctorName ||
              referBookData?.fromDoctorQualification ||
              referBookData?.fromDoctorContactNumber) && (
                <Grid container spacing={1} mb={2}>
                  {(referBookData?.fromDoctorName &&
                    <Grid item xs={6}>
                      <Typography sx={filedsLabelText}>
                        Ref By Doctor (Your Name):
                      </Typography>
                      <Typography variant="body1" sx={value}>
                        {referBookData?.fromDoctorName}
                      </Typography>
                    </Grid>
                  )}

                  {(referBookData?.fromDoctorQualification &&
                    <Grid item xs={6}>
                      <Typography sx={filedsLabelText}>
                        Qualification:
                      </Typography>
                      <Typography variant="body1" sx={value}>
                        {referBookData?.fromDoctorQualification}
                      </Typography>
                    </Grid>
                  )}

                  {(referBookData?.fromDoctorContactNumber &&
                    <Grid item xs={6}>
                      <Typography sx={filedsLabelText}>
                        Contact Number:
                      </Typography>
                      <Typography variant="body1" sx={value}>
                        {referBookData?.fromDoctorContactNumber}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              width: "100%",
            }}
          >
            <Typography
              className="font-size-11"
              sx={{ whiteSpace: "nowrap", marginTop: "2px" }}
            >
              Powered by
            </Typography>
            <a href="/" target="_blank" rel="noopener noreferrer">
              <img
                src={logo}
                alt="Logo"
                style={{
                  height: 20,
                  width: "auto",
                  objectFit: "contain",
                  verticalAlign: "middle",
                  cursor: "pointer",
                }}
              />
            </a>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pb: 2,
            flexDirection: smallScreen ? "column" : "row",
            gap: smallScreen ? 1 : 0,
          }}>
          <Typography className="font-size-11">
            Copyrights &copy; {new Date().getFullYear()} - Pediaverse
            Solutions Pvt Ltd
          </Typography>

          <Typography className="font-size-11">
            <a
              href="/terms"
              target="_blank"
              className="blue-link"
              style={{ textDecoration: "none" }}>
              Terms of Service
            </a>{" "}
            &nbsp;
            <a
              href="/privacy-policy"
              target="_blank"
              className="blue-link"
              style={{ textDecoration: "none" }}>
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </SkeletonWrapper>
    </>
  )
}

export default ReferBookReport
